import { useEffect, useState } from 'react';
import { Alert, Button, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../redux/features/auth/axiosAuthHelper';
import { type RootState } from '../../redux/store';
import checkRoutePermissions from '../../routes/appRoutePermissions';
import { apiError } from '../../services/apiError';
import { validAccount, type CustomerAccount } from './accountApi';
import AccountForm from './AccountForm';

export default function CustomerAccountDetailPage() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const auth = useSelector((state: RootState) => state.auth);
  const allowed =
    checkRoutePermissions(auth, { state: 'customer-accounts' }) === true;
  const [account, setAccount] = useState<CustomerAccount | null>(null);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    setAccount(null);
    setError('');
    if (!accountId || !/^[1-9]\d*$/.test(accountId)) {
      setError('Account not found.');
      return;
    }
    api
      .get(`customer-accounts/${accountId}/`, {
        headers: { Authorization: `Bearer ${auth.access}` },
        signal: controller.signal,
        timeout: 30000,
      })
      .then(({ data }) => {
        if (!validAccount(data) || String(data.id) !== accountId)
          throw new Error('Invalid account');
        if (!controller.signal.aborted) setAccount(data);
      })
      .catch((failure) => {
        if (!controller.signal.aborted)
          setError(
            apiError(failure, 'Customer account')?.message ||
              'Account could not be loaded.',
          );
      });
    return () => controller.abort();
  }, [accountId, allowed, auth.access, reload]);
  if (!allowed)
    return (
      <Alert severity="error">
        You do not have permission to view customer accounts.
      </Alert>
    );
  if (error)
    return (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            onClick={() => setReload((value) => value + 1)}
          >
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  return account ? (
    <AccountForm
      key={`${account.id}-${reload}`}
      account={account}
      onClose={() => navigate('/customer-accounts')}
      onSaved={() => setReload((value) => value + 1)}
    />
  ) : (
    <CircularProgress aria-label="Loading customer account" />
  );
}
